import type { KeyboardEvent, ReactNode } from 'react'
import { useId, useRef } from 'react'
import styles from './Tabs.module.css'

export interface TabItem {
  id: string
  label: ReactNode
  /** Count pill (e.g. filings per type). */
  count?: string | number
  disabled?: boolean
}

export interface TabsProps {
  tabs: TabItem[]
  value: string
  onChange: (id: string) => void
  /** Accessible name for the tab list. */
  label: string
  /** `underline` (page sections, filing types) or `pill` (watchlists). */
  variant?: 'underline' | 'pill'
  /** Content for the active tab. Omit when tabs filter content rendered elsewhere, and pass `controls`. */
  renderPanel?: (id: string) => ReactNode
  /** id of the element the tabs control when `renderPanel` is not used. */
  controls?: string
  className?: string
}

/** WAI-ARIA tabs: ←/→ move and activate, Home/End jump; only the active tab is in the tab order. */
export function Tabs({ tabs, value, onChange, label, variant = 'underline', renderPanel, controls, className }: TabsProps) {
  const base = useId()
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const enabled = tabs.map((t, i) => (t.disabled ? -1 : i)).filter(i => i >= 0)

  const focusAt = (i: number) => {
    const tab = tabs[i]
    if (!tab) return
    refs.current[i]?.focus()
    onChange(tab.id)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const pos = enabled.indexOf(index)
    let next: number | undefined
    if (e.key === 'ArrowRight') next = enabled[(pos + 1) % enabled.length]
    else if (e.key === 'ArrowLeft') next = enabled[(pos - 1 + enabled.length) % enabled.length]
    else if (e.key === 'Home') next = enabled[0]
    else if (e.key === 'End') next = enabled[enabled.length - 1]
    if (next !== undefined) {
      e.preventDefault()
      focusAt(next)
    }
  }

  const panelId = `${base}-panel`
  return (
    <div className={className}>
      <div role="tablist" aria-label={label} className={`${styles.list} ${styles[variant]}`}>
        {tabs.map((t, i) => {
          const selected = t.id === value
          return (
            <button
              key={t.id}
              ref={el => { refs.current[i] = el }}
              type="button"
              role="tab"
              id={`${base}-tab-${t.id}`}
              aria-selected={selected}
              aria-controls={renderPanel ? panelId : controls}
              tabIndex={selected ? 0 : -1}
              disabled={t.disabled}
              className={`${styles.tab} ${selected ? styles.selected : ''}`}
              onClick={() => onChange(t.id)}
              onKeyDown={e => onKeyDown(e, i)}
            >
              {t.label}
              {t.count !== undefined && <span className={styles.count}>{t.count}</span>}
            </button>
          )
        })}
      </div>
      {renderPanel && (
        <div role="tabpanel" id={panelId} aria-labelledby={`${base}-tab-${value}`} tabIndex={0} className={styles.panel}>
          {renderPanel(value)}
        </div>
      )}
    </div>
  )
}
